// Component for editting an entry in the CompletedTasks table.
//
//   entry - CompletedTask to edit
//   item - Item task belongs to
//   handleSave - async function to save the task to the DB
//   handleDelete - async function to delete the task from the DB
//   children - Optional extra HTML to inject after the edit form.

import { DataCard, EditForm, Attachments } from '../../components';
import { urlForLogbookEntryStorage } from '../../fleet-shared/Urls.mjs';
import { buildFieldsForItem } from '../../fleet-shared/Common.mjs'
import { validateRequiredName } from '../../utilities/Validators.mjs';
import {
  deriveEntryFields,
  fieldsForType,
  flattenEntry,
  splitEntryFields,
  LOGBOOK_TYPE_MAINTENANCE,
} from '../../fleet-shared/LogbookTypes.mjs';


export const LogbookEditEntry = ({ entry, item, logbook, handleSave, handleDelete, children }) => {
  const typeId = logbook?.logbookType || LOGBOOK_TYPE_MAINTENANCE;
  // Clone the type's fields (buildFieldsForItem mutates them to add the counter
  // select options) so we never mutate the shared registry.
  const fields = buildFieldsForItem(fieldsForType(typeId).map((f) => ({ ...f })), item);
  // Only types with a `name` column need it held when emptied.
  const validate = fields.some((f) => f.key === 'name') ? validateRequiredName : undefined;

  // EditForm edits the FLAT entry, so its output has this type's json-backed
  // keys sitting at the top level -- re-nest them before the consumer's save
  // hands the object to updateCompletedTask, or they'd be posted as mutation
  // attributes that don't exist. `preserve` keeps blob keys this registry
  // doesn't know about (the geo route) alive across the write.
  //
  // Only Maintenance reaches this component today (it's the from-a-task page,
  // and only Maintenance supports task completion) and Maintenance has no
  // json-backed fields at all, so this is a no-op there -- which is exactly
  // why it was safe to be missing, and why it shouldn't stay missing.
  async function handleSaveEntry(edited) {
    const split = splitEntryFields(edited, typeId, { preserve: entry?.fields });
    await handleSave(split);
  }

  return (entry !== null) && (
    <DataCard
      header=<strong>Edit Logbook Entry</strong>
      body=<EditForm
              item={flattenEntry(entry, typeId)}
              fields={fields}
              saveItem={handleSaveEntry}
              handleSecondaryButton={handleDelete}
              secondaryButtonText='Delete'
              secondaryButtonVariant='danger'
              deriveState={(previous, next) => deriveEntryFields(previous, next, typeId)}
              validate={validate}
              recordKeyPrefix="entry"
            >
              <Attachments bucket={urlForLogbookEntryStorage(item.id, entry.id)} />
              {children}
            </EditForm>
    />
  );
}